// WINDOWED FEEDER machine_card fixture (ME-MACHINEID-RENDER SP-1b).
// Extends the canonical FEEDER shape with the PINNED per-figure `windows[]`
// (bot ask #1, commit `d3283df`): the presentation-NOK HERO carries all FIVE
// window entries in fixed order — a mix of `ok` reads and ONE `absent` window
// (`this_week`) to exercise the honesty rule (absent => "not available", NEVER a
// scaled number). A second SNAPSHOT figure (`f_run_state`, latest-wins) carries
// NO `windows[]` — it IGNORES the pill selector and always shows its current
// state. Card freshness stays STALE + historical_backfill (proves the two-clock
// split + §7A #1: window switching must NOT recompute freshness).
import type { MachineCardArtifactData } from "../../../chatBot/models/machineEvidenceModels";

export const WINDOWED_FEEDER_MACHINE_GUID = "d72d27d9-0308-49e6-7c19-08dec02ae617";

/** The hero's per-window rollup values, exported so tests can assert the exact
 *  rendered number per window without duplicating literals. `this_week` is the
 *  no-data (`absent`) window. */
export const WINDOWED_HERO_VALUES = {
  last_hour: 20,
  this_shift: 512,
  today: 2172,
  this_week: null,
  all_time: 158934,
} as const;

export const WINDOWED_FEEDER_MACHINE_CARD: MachineCardArtifactData = {
  schema_version: 1,
  mode: "snapshot",
  machine: {
    machine: WINDOWED_FEEDER_MACHINE_GUID,
    machine_id_full: WINDOWED_FEEDER_MACHINE_GUID,
    class_template: null,
    display_name: "MASHi2601",
  },
  freshness: {
    state: "STALE",
    as_of: "2026-06-30T16:33:00+00:00",
    last_sample_at: "2026-06-29T18:08:27.134590+00:00",
    age_seconds: 73454,
    source: "mash_liveness",
    provenance: "historical_backfill",
    event_time: "2026-06-10T09:24:53.263000+00:00",
  },
  tabs: [
    {
      part_slug: "feeders",
      name: "Feeders",
      parent_slug: null,
      verdict: {
        status: "stale",
        headline: "Feeder presentation: 2172 NOK occurrences today across 4 bowl(s)",
        detail: null,
        basis: "occurrence count (no calibrated threshold this milestone)",
      },
      lane_coverage: {},
      figures: [
        {
          // WINDOWED hero — carries all five window rollups (fixed order).
          id: "f_feeder_presentation_nok",
          label: "Feeder presentation NOK",
          value: 2172,
          unit: null,
          window: "2026-06-10 → 2026-06-10",
          window_start: "2026-06-10T00:00:00+02:00",
          window_end: "2026-06-11T00:00:00+02:00",
          denominator: null,
          coverage: "ok",
          measure_basis: null,
          threshold: null,
          n: 2172,
          source_ref: "s_feeder_presentation_nok",
          freshness: null,
          breakdown: null,
          windows: [
            { window_key: "last_hour", value: 20, count: 20, coverage: "ok", source_ref: "s_feeder_presentation_nok" },
            { window_key: "this_shift", value: 512, count: 512, coverage: "ok", source_ref: "s_feeder_presentation_nok" },
            { window_key: "today", value: 2172, count: 2172, coverage: "ok", source_ref: "s_feeder_presentation_nok" },
            // No data for the week window => absent (NOT an omitted entry, NOT a scaled 0).
            { window_key: "this_week", value: null, count: null, coverage: "absent", source_ref: "s_feeder_presentation_nok" },
            { window_key: "all_time", value: 158934, count: 158934, coverage: "ok", source_ref: "s_feeder_presentation_nok" },
          ],
        },
        {
          // SNAPSHOT (latest-wins) figure — NO windows[]. Ignores the selector.
          id: "f_run_state",
          label: "Run state",
          value: "RUNNING",
          unit: null,
          window: null,
          window_start: null,
          window_end: null,
          denominator: null,
          coverage: "ok",
          measure_basis: null,
          threshold: null,
          n: null,
          source_ref: "s_run_state",
          freshness: null,
          breakdown: null,
        },
      ],
      trend: null,
      live: null,
    },
  ],
  sources: [
    {
      id: "s_feeder_presentation_nok",
      signal_key: "feeder_presentation_nok",
      window: "2026-06-10 → 2026-06-10",
      window_start: "2026-06-10T00:00:00+02:00",
      window_end: "2026-06-11T00:00:00+02:00",
      count: 2172,
      source_grain: "rollup_cagg",
      read_source: "telemetry store",
      edge_path: null,
    },
    {
      id: "s_run_state",
      signal_key: "run_state",
      window: null,
      window_start: null,
      window_end: null,
      count: null,
      source_grain: "live_snapshot",
      read_source: "telemetry store",
      edge_path: null,
    },
  ],
  frozen: { is_frozen: false, frozen_at: null },
};
